import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

@observer
class CoffeeDashboard extends Component {
    componentDidMount() {
        this.loadStats();
    }

    loadStats = async () => {
        const { coffeeStore } = this.props.stores;
        await coffeeStore.loadStats();
    }

    openCoffee = async (id) => {
        const { appState, coffeeStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        await coffeeStore.load(id);
        appState.openDrawer('viewCoffee', {
            id,
            height: drawerHeightMediumLarge,
            stores: this.props.stores,
            callback: () => this.loadStats(),
        });
    }

    quickRateCoffee = async (id) => {
        const { appState, coffeeStore } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        await coffeeStore.load(id);
        appState.openDrawer('quickRateCoffee', {
            id,
            height: drawerHeightMediumLarge,
            stores: this.props.stores,
            callback: () => this.loadStats(),
        });
    }

    render() {
        const { coffeeStore } = this.props.stores;
        const { stats = {} } = coffeeStore;

        const {
            totalCoffees = 0,
            weightStats = {},
            yearsWithCoffee = 0,
            firstYear = null,
            lastYear = null,
            currentPeriod = null,
            currentCoffees = [],
            countriesCount = 0,
            roastersCount = 0,
            producersCount = 0,
            machinesCount = 0,
            uniquePeriods = 0,
            totalCups = 0,
            topRatedCoffee = null,
            lowestRatedCoffee = null,
            ratingDistribution = {},
            bestMonth = null,
            countriesBreakdown = [],
            processBreakdown = [],
            roasterBreakdown = [],
            machineBreakdown = [],
            averageRatings = {},
            averageCuppingScore = 0,
            averageUserScore = 0,
            flavorWords = [],
            altitudeStats = {},
            harvestBreakdown = [],
            varietyBreakdown = [],
            locationBreakdown = [],
            scoresByCountry = [],
            scoresByVariety = [],
            scoresByProcess = [],
            scoresByLocation = [],
            scoresByRoaster = [],
            scoresByFlavor = [],
        } = stats;

        // Calculate max for rating distribution bar chart
        const maxRatingCount = Math.max(...Object.values(ratingDistribution), 1);

        return (
            <div class='coffee-dashboard'>
                {/* Epic Hero Banner */}
                <div class='card mb-4 border-0 overflow-hidden' style='background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);'>
                    <div class='card-body py-4'>
                        <div class='row align-items-center'>
                            <div class='col-12 text-center mb-3'>
                                <h1 class='display-5 text-white mb-1' style='font-weight: 300;'>
                                    <i class='fa-solid fa-mug-hot me-3' style='color: #e94560;' />
                                    Your Coffee Journey
                                </h1>
                                {firstYear && lastYear && (
                                    <p class='text-white-50 mb-0'>{firstYear} - {lastYear}</p>
                                )}
                            </div>
                        </div>

                        {/* Main Stats Row */}
                        <div class='row g-3 text-center'>
                            <div class='col-4 col-md-2'>
                                <div class='p-2'>
                                    <div class='display-5 fw-bold' style='color: #e94560;'>{yearsWithCoffee}</div>
                                    <small class='text-white-50'>Years</small>
                                </div>
                            </div>
                            <div class='col-4 col-md-2'>
                                <div class='p-2'>
                                    <div class='display-5 fw-bold' style='color: #f39c12;'>{totalCoffees}</div>
                                    <small class='text-white-50'>Coffees</small>
                                </div>
                            </div>
                            <div class='col-4 col-md-2'>
                                <div class='p-2'>
                                    <div class='display-5 fw-bold' style='color: #00cec9;'>{totalCups}</div>
                                    <small class='text-white-50'>Cups</small>
                                </div>
                            </div>
                            <div class='col-4 col-md-2'>
                                <div class='p-2'>
                                    <div class='display-5 fw-bold' style='color: #a29bfe;'>{countriesCount}</div>
                                    <small class='text-white-50'>Countries</small>
                                </div>
                            </div>
                            <div class='col-4 col-md-2'>
                                <div class='p-2'>
                                    <div class='display-5 fw-bold' style='color: #55efc4;'>{roastersCount}</div>
                                    <small class='text-white-50'>Roasters</small>
                                </div>
                            </div>
                            <div class='col-4 col-md-2'>
                                <div class='p-2'>
                                    <div class='display-5 fw-bold' style='color: #fd79a8;'>{producersCount}</div>
                                    <small class='text-white-50'>Farms</small>
                                </div>
                            </div>
                        </div>

                        {/* Secondary Stats */}
                        <div class='row mt-3 text-center'>
                            <div class='col-6 col-md'>
                                <div class='d-flex align-items-center justify-content-center text-white-50'>
                                    <i class='fa-solid fa-weight-scale me-2' />
                                    <span>{weightStats.totalKg || 0} kg</span>
                                </div>
                            </div>
                            <div class='col-6 col-md'>
                                <div class='d-flex align-items-center justify-content-center text-white-50'>
                                    <i class='fa-solid fa-box me-2' />
                                    <span>{uniquePeriods} deliveries</span>
                                </div>
                            </div>
                            <div class='col-6 col-md'>
                                <div class='d-flex align-items-center justify-content-center text-white-50'>
                                    <i class='fa-solid fa-gear me-2' style='color: #74b9ff;' />
                                    <span>{machinesCount} machines</span>
                                </div>
                            </div>
                            <div class='col-6 col-md'>
                                <div class='d-flex align-items-center justify-content-center text-white-50'>
                                    <i class='fa-solid fa-star me-2' style='color: #f39c12;' />
                                    <span>{averageUserScore || '-'}/10 avg</span>
                                </div>
                            </div>
                            {averageCuppingScore > 0 && (
                                <div class='col-6 col-md'>
                                    <div class='d-flex align-items-center justify-content-center text-white-50'>
                                        <i class='fa-solid fa-award me-2' style='color: #00cec9;' />
                                        <span>{averageCuppingScore} cupping</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Current Delivery */}
                {currentCoffees.length > 0 && (
                    <div class='card mb-4 border-0' style='background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);'>
                        <div class='card-header bg-transparent border-0 text-white py-2'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-truck-fast me-2' />
                                Current Delivery: {currentPeriod}
                            </h5>
                        </div>
                        <div class='card-body pt-0 pb-2'>
                            <div class='row g-2'>
                                {currentCoffees.map(coffee => (
                                    <div class='col-12 col-md-6' key={coffee.id}>
                                        <div
                                            class='card h-100 border-0 shadow-sm'
                                            style='cursor: pointer;'
                                            onClick={() => this.openCoffee(coffee.id)}
                                        >
                                            <div class='card-body p-2'>
                                                <div class='d-flex justify-content-between align-items-start mb-1'>
                                                    <h6 class='card-title mb-0 flex-grow-1'>{coffee.name}</h6>
                                                    <div class='d-flex gap-1'>
                                                        {coffee.cuppingScore && (
                                                            <span class='badge bg-info' title='Cupping Score'>
                                                                {coffee.cuppingScore}
                                                            </span>
                                                        )}
                                                        <span class={`badge ${coffee.score ? 'bg-warning text-dark' : 'bg-secondary'}`} title='Your Rating'>
                                                            {coffee.score ? `${coffee.score}/10` : '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div class='small text-muted'>
                                                    <i class='fa-solid fa-globe me-1 text-primary' />
                                                    {coffee.country}
                                                    {coffee.location && <span class='opacity-75'> · {coffee.location}</span>}
                                                </div>
                                                <div class='d-flex justify-content-between align-items-center mt-1'>
                                                    {coffee.flavor ? (
                                                        <div class='small text-warning flex-grow-1 me-2'>
                                                            <i class='fa-solid fa-lemon me-1' />
                                                            {coffee.flavor}
                                                        </div>
                                                    ) : <div />}
                                                    <button
                                                        class='btn btn-sm btn-outline-warning py-0 flex-shrink-0'
                                                        style='font-size: 0.75rem;'
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            this.quickRateCoffee(coffee.id);
                                                        }}
                                                    >
                                                        <i class='fa-solid fa-star me-1' />
                                                        Rate
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Top Rated Coffee & Best Month */}
                <div class='row g-3 mb-4'>
                    {topRatedCoffee && (
                        <div class='col-12 col-md-6'>
                            <div
                                class='card h-100 border-0 cursor-pointer'
                                style='background: linear-gradient(135deg, #f39c12 0%, #e74c3c 100%); cursor: pointer;'
                                onClick={() => this.openCoffee(topRatedCoffee.id)}
                            >
                                <div class='card-body text-white'>
                                    <div class='d-flex align-items-start'>
                                        <div class='flex-grow-1'>
                                            <div class='d-flex align-items-center mb-2'>
                                                <i class='fa-solid fa-trophy fa-2x me-3' />
                                                <div>
                                                    <small class='opacity-75'>Your Best Coffee</small>
                                                    <h4 class='mb-0'>{topRatedCoffee.name}</h4>
                                                </div>
                                            </div>
                                            <p class='mb-0 small opacity-75'>
                                                {topRatedCoffee.country}
                                                {topRatedCoffee.roastery && ` by ${topRatedCoffee.roastery}`}
                                            </p>
                                        </div>
                                        <div class='text-end'>
                                            <div class='display-4 fw-bold'>{topRatedCoffee.score}</div>
                                            <small>/10</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {bestMonth && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100 border-0' style='background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);'>
                                <div class='card-body text-white'>
                                    <div class='d-flex align-items-start'>
                                        <div class='flex-grow-1'>
                                            <div class='d-flex align-items-center mb-2'>
                                                <i class='fa-solid fa-calendar-star fa-2x me-3' />
                                                <div>
                                                    <small class='opacity-75'>Best Delivery</small>
                                                    <h4 class='mb-0'>{bestMonth.month}</h4>
                                                </div>
                                            </div>
                                            <p class='mb-0 small opacity-75'>
                                                {bestMonth.count} coffees rated
                                            </p>
                                        </div>
                                        <div class='text-end'>
                                            <div class='display-4 fw-bold'>{bestMonth.avgScore}</div>
                                            <small>avg</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Rating Distribution */}
                {Object.values(ratingDistribution).some(v => v > 0) && (
                    <div class='card mb-4'>
                        <div class='card-header'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-chart-bar me-2' />
                                Your Rating Distribution
                            </h5>
                        </div>
                        <div class='card-body'>
                            <div class='d-flex align-items-end justify-content-between' style='height: 120px;'>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => {
                                    const count = ratingDistribution[score] || 0;
                                    const height = count > 0 ? Math.max((count / maxRatingCount) * 100, 10) : 5;
                                    const color = score >= 8 ? '#2ecc71' : score >= 6 ? '#f39c12' : score >= 4 ? '#e67e22' : '#e74c3c';
                                    return (
                                        <div class='text-center flex-fill mx-1' key={score}>
                                            <div
                                                class='rounded-top mx-auto'
                                                style={`height: ${height}px; width: 100%; max-width: 40px; background: ${color}; transition: height 0.3s;`}
                                                title={`${count} coffees rated ${score}/10`}
                                            />
                                            <small class='text-muted d-block mt-1'>{score}</small>
                                            <small class='text-muted d-block' style='font-size: 0.7rem;'>{count || '-'}</small>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Countries with Progress Bars */}
                {countriesBreakdown.length > 0 && (
                    <div class='card mb-4'>
                        <div class='card-header'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-earth-americas me-2' />
                                Origins Explored
                            </h5>
                        </div>
                        <div class='card-body'>
                            <div class='row'>
                                {countriesBreakdown.slice(0, 8).map((item, i) => (
                                    <div class='col-6 col-md-3 mb-3' key={item.country}>
                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                            <span class='fw-medium'>
                                                {i === 0 && <i class='fa-solid fa-crown text-warning me-1' />}
                                                {item.country}
                                            </span>
                                            <span class='badge bg-dark'>{item.count}</span>
                                        </div>
                                        <div class='progress' style='height: 6px;'>
                                            <div
                                                class='progress-bar'
                                                style={`width: ${(item.count / totalCoffees) * 100}%; background: linear-gradient(90deg, #667eea, #764ba2);`}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Altitude Stats - Visual */}
                {altitudeStats.count > 0 && (
                    <div class='card mb-4 border-0' style='background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);'>
                        <div class='card-body text-white'>
                            <h5 class='mb-3'>
                                <i class='fa-solid fa-mountain-sun me-2' />
                                Altitude Journey
                            </h5>
                            <div class='row text-center'>
                                <div class='col-4'>
                                    <div class='mb-2'>
                                        <i class='fa-solid fa-arrow-down fa-2x opacity-75' />
                                    </div>
                                    <h3 class='mb-0'>{altitudeStats.min.toLocaleString()}</h3>
                                    <small class='opacity-75'>Lowest (masl)</small>
                                </div>
                                <div class='col-4'>
                                    <div class='mb-2'>
                                        <i class='fa-solid fa-crosshairs fa-2x' style='color: #2ecc71;' />
                                    </div>
                                    <h3 class='mb-0' style='color: #2ecc71;'>{altitudeStats.avg.toLocaleString()}</h3>
                                    <small class='opacity-75'>Average</small>
                                </div>
                                <div class='col-4'>
                                    <div class='mb-2'>
                                        <i class='fa-solid fa-arrow-up fa-2x opacity-75' />
                                    </div>
                                    <h3 class='mb-0'>{altitudeStats.max.toLocaleString()}</h3>
                                    <small class='opacity-75'>Highest (masl)</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Flavor Cloud */}
                {flavorWords.length > 0 && (
                    <div class='card mb-4'>
                        <div class='card-header'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-cloud me-2' />
                                Flavor Cloud
                            </h5>
                        </div>
                        <div class='card-body text-center'>
                            <div class='d-flex flex-wrap gap-2 justify-content-center'>
                                {flavorWords.slice(0, 20).map((item, i) => {
                                    const size = Math.max(0.75, 1.6 - (i * 0.04));
                                    const colors = ['#e74c3c', '#f39c12', '#2ecc71', '#3498db', '#9b59b6', '#1abc9c', '#e67e22'];
                                    const color = colors[i % colors.length];
                                    return (
                                        <span
                                            key={item.word}
                                            class='badge'
                                            style={`font-size: ${size}rem; background: ${color}; opacity: ${1 - (i * 0.02)};`}
                                        >
                                            {item.word}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Favorite Flavors */}
                {scoresByFlavor.length > 0 && (
                    <div class='card mb-4 border-warning'>
                        <div class='card-header' style='background: linear-gradient(90deg, #f39c12, #e74c3c); color: white;'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-heart me-2' />
                                Flavors You Love Most
                            </h5>
                        </div>
                        <div class='card-body'>
                            <div class='row'>
                                {scoresByFlavor.slice(0, 6).map((item, i) => (
                                    <div class='col-6 col-md-4 mb-3' key={item.flavor}>
                                        <div class='d-flex justify-content-between align-items-center'>
                                            <span class='text-capitalize fw-medium'>
                                                {i === 0 && <i class='fa-solid fa-heart text-danger me-1' />}
                                                {i === 1 && <i class='fa-solid fa-heart text-danger opacity-75 me-1' />}
                                                {i === 2 && <i class='fa-solid fa-heart text-danger opacity-50 me-1' />}
                                                {item.flavor}
                                            </span>
                                            <span class='badge bg-warning text-dark'>{item.avgScore}</span>
                                        </div>
                                        <div class='progress mt-1' style='height: 4px;'>
                                            <div
                                                class='progress-bar bg-warning'
                                                style={`width: ${(item.avgScore / 10) * 100}%`}
                                            />
                                        </div>
                                        <small class='text-muted'>{item.count} coffees</small>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Processing Methods & Varieties Side by Side */}
                <div class='row g-3 mb-4'>
                    {processBreakdown.length > 0 && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100'>
                                <div class='card-header'>
                                    <h5 class='mb-0'>
                                        <i class='fa-solid fa-flask me-2' />
                                        Processing Methods
                                    </h5>
                                </div>
                                <div class='card-body'>
                                    {processBreakdown.slice(0, 5).map((item, i) => {
                                        const colors = ['#e74c3c', '#f39c12', '#3498db', '#2ecc71', '#9b59b6'];
                                        return (
                                            <div class='mb-2' key={item.process}>
                                                <div class='d-flex justify-content-between mb-1'>
                                                    <span>{item.process}</span>
                                                    <span class='badge' style={`background: ${colors[i]};`}>{item.count}</span>
                                                </div>
                                                <div class='progress' style='height: 8px;'>
                                                    <div
                                                        class='progress-bar'
                                                        style={`width: ${(item.count / totalCoffees) * 100}%; background: ${colors[i]};`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                    {varietyBreakdown.length > 0 && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100'>
                                <div class='card-header'>
                                    <h5 class='mb-0'>
                                        <i class='fa-solid fa-seedling me-2' />
                                        Top Varieties
                                    </h5>
                                </div>
                                <div class='card-body'>
                                    {varietyBreakdown.slice(0, 5).map((item, i) => {
                                        const colors = ['#27ae60', '#2ecc71', '#1abc9c', '#16a085', '#3498db'];
                                        return (
                                            <div class='mb-2' key={item.variety}>
                                                <div class='d-flex justify-content-between mb-1'>
                                                    <span>{item.variety}</span>
                                                    <span class='badge' style={`background: ${colors[i]};`}>{item.count}</span>
                                                </div>
                                                <div class='progress' style='height: 8px;'>
                                                    <div
                                                        class='progress-bar'
                                                        style={`width: ${(item.count / totalCoffees) * 100}%; background: ${colors[i]};`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Roasters & Machines Side by Side */}
                <div class='row g-3 mb-4'>
                    {roasterBreakdown.length > 0 && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100'>
                                <div class='card-header'>
                                    <h5 class='mb-0'>
                                        <i class='fa-solid fa-fire-flame-curved me-2' />
                                        Your Roasters
                                    </h5>
                                </div>
                                <div class='card-body'>
                                    {roasterBreakdown.slice(0, 5).map((item, i) => {
                                        const colors = ['#f39c12', '#95a5a6', '#cd7f32', '#3498db', '#2ecc71'];
                                        return (
                                            <div class='mb-2' key={item.roaster}>
                                                <div class='d-flex justify-content-between mb-1'>
                                                    <span class='text-truncate'>
                                                        {i === 0 && <i class='fa-solid fa-medal text-warning me-1' />}
                                                        {i === 1 && <i class='fa-solid fa-medal text-secondary me-1' />}
                                                        {i === 2 && <i class='fa-solid fa-medal me-1' style='color: #cd7f32;' />}
                                                        {item.roaster}
                                                    </span>
                                                    <span class='badge' style={`background: ${colors[i]};`}>{item.count}</span>
                                                </div>
                                                <div class='progress' style='height: 6px;'>
                                                    <div
                                                        class='progress-bar'
                                                        style={`width: ${(item.count / totalCoffees) * 100}%; background: ${colors[i]};`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                    {machineBreakdown.length > 0 && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100'>
                                <div class='card-header'>
                                    <h5 class='mb-0'>
                                        <i class='fa-solid fa-gears me-2' />
                                        Roaster Machines
                                    </h5>
                                </div>
                                <div class='card-body'>
                                    {machineBreakdown.slice(0, 5).map((item, i) => {
                                        const colors = ['#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12'];
                                        return (
                                            <div class='mb-2' key={item.machine}>
                                                <div class='d-flex justify-content-between mb-1'>
                                                    <span class='text-truncate'>
                                                        {i === 0 && <i class='fa-solid fa-trophy text-warning me-1' />}
                                                        {item.machine}
                                                    </span>
                                                    <span class='badge' style={`background: ${colors[i]};`}>{item.count}</span>
                                                </div>
                                                <div class='progress' style='height: 6px;'>
                                                    <div
                                                        class='progress-bar'
                                                        style={`width: ${(item.count / totalCoffees) * 100}%; background: ${colors[i]};`}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Harvest Seasons */}
                {harvestBreakdown.length > 0 && (
                    <div class='card mb-4'>
                        <div class='card-header'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-calendar-days me-2' />
                                Harvest Seasons
                            </h5>
                        </div>
                        <div class='card-body'>
                            <div class='d-flex flex-wrap gap-2'>
                                {harvestBreakdown.slice(0, 12).map(item => (
                                    <span class='badge bg-warning text-dark fs-6' key={item.harvest}>
                                        {item.harvest} ({item.count})
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Regions */}
                {locationBreakdown.length > 0 && (
                    <div class='card mb-4'>
                        <div class='card-header'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-map-location-dot me-2' />
                                Regions Explored
                            </h5>
                        </div>
                        <div class='card-body'>
                            <div class='d-flex flex-wrap gap-2'>
                                {locationBreakdown.slice(0, 15).map(item => (
                                    <span class='badge bg-info fs-6' key={item.location}>
                                        {item.location} ({item.count})
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Average Tasting Ratings - Radar Style */}
                {Object.keys(averageRatings).length > 0 && (
                    <div class='card mb-4'>
                        <div class='card-header'>
                            <h5 class='mb-0'>
                                <i class='fa-solid fa-wine-glass me-2' />
                                Tasting Profile
                            </h5>
                        </div>
                        <div class='card-body'>
                            <div class='row'>
                                {Object.entries(averageRatings).map(([key, value]) => {
                                    const label = key === 'score' ? 'Overall' : key.replace('Rating', '');
                                    const isOverall = key === 'score';
                                    const color = isOverall ? '#f39c12' : '#3498db';
                                    return (
                                        <div class={isOverall ? 'col-12 mb-3' : 'col-6 col-md-4 mb-3'} key={key}>
                                            <div class='d-flex justify-content-between'>
                                                <span class={`text-capitalize ${isOverall ? 'fw-bold' : ''}`}>{label}</span>
                                                <span class='fw-bold' style={`color: ${color};`}>{value}/10</span>
                                            </div>
                                            <div class='progress' style={`height: ${isOverall ? '12px' : '8px'};`}>
                                                <div
                                                    class='progress-bar'
                                                    style={`width: ${(value / 10) * 100}%; background: ${color};`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Score Leaderboards */}
                <div class='row g-3 mb-4'>
                    {scoresByCountry.length > 0 && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100'>
                                <div class='card-header'>
                                    <h5 class='mb-0'>
                                        <i class='fa-solid fa-ranking-star me-2' />
                                        Best Countries
                                    </h5>
                                </div>
                                <div class='card-body'>
                                    {scoresByCountry.slice(0, 5).map((item, i) => (
                                        <div class='d-flex align-items-center mb-2' key={item.name}>
                                            <span class='badge bg-dark me-2' style='width: 24px;'>{i + 1}</span>
                                            <span class='flex-grow-1'>{item.name}</span>
                                            <span class='badge bg-warning text-dark'>{item.avgScore}/10</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {scoresByRoaster.length > 0 && (
                        <div class='col-12 col-md-6'>
                            <div class='card h-100'>
                                <div class='card-header'>
                                    <h5 class='mb-0'>
                                        <i class='fa-solid fa-ranking-star me-2' />
                                        Best Roasters
                                    </h5>
                                </div>
                                <div class='card-body'>
                                    {scoresByRoaster.slice(0, 5).map((item, i) => (
                                        <div class='d-flex align-items-center mb-2' key={item.name}>
                                            <span class='badge bg-dark me-2' style='width: 24px;'>{i + 1}</span>
                                            <span class='flex-grow-1 text-truncate'>{item.name}</span>
                                            <span class='badge bg-warning text-dark'>{item.avgScore}/10</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default CoffeeDashboard;
